/**
 * Offline Startup Screen
 * 
 * Shown when the PWA is opened without internet and there's no
 * cached bootstrap data available. This happens on first use or
 * if the cache has expired.
 */

import { WifiOff, RefreshCw, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface OfflineStartupScreenProps {
  onRetry: () => void;
  isRetrying?: boolean;
}

export function OfflineStartupScreen({ onRetry, isRetrying = false }: OfflineStartupScreenProps) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <WifiOff className="h-8 w-8 text-destructive" />
          </div>
          <CardTitle className="text-xl">Sem Conexão</CardTitle>
          <CardDescription className="text-base">
            Não foi possível inicializar o sistema
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-lg bg-muted p-4 text-sm text-muted-foreground">
            <p className="mb-3">
              Para usar o PDV offline, você precisa ter acessado o sistema pelo menos 
              uma vez com internet.
            </p>
            <div className="flex items-start gap-2">
              <Smartphone className="h-4 w-4 mt-0.5 shrink-0" />
              <p>
                Após o primeiro acesso, o sistema armazena os dados necessários para 
                funcionar sem internet.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <Button 
              className="w-full" 
              onClick={onRetry}
              disabled={isRetrying}
            >
              {isRetrying ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Verificando conexão...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Tentar Novamente
                </>
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              Verifique sua conexão Wi-Fi ou dados móveis e tente novamente.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
